/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChronoUnit, LocalDateTime } from 'js-joda';
import {
  max as _max,
  min as _min,
  filter,
  fromPairs,
  has,
  head,
  isEmpty,
  keys,
  map,
  maxBy,
  pickBy,
  sortBy,
  uniq,
} from 'es-toolkit/compat';

import { BookingCard } from 'components/business/search/booking-card/BookingCard';
import { DisplayDate } from '@tock/time';
import { IWithContextProps, consumer, core, withContext } from '@tock/coretypes';
import { LockOptions } from 'types';
import { ReservationSearchResultsProps } from 'components/business/search/ReservationSearchResults';
import {
  SearchTimeMap,
  Stats,
  TicketGroupStatus,
  ticketGroupStatusForPurchaseSize,
} from 'utils/calendar';
import { Typography } from 'omakase';
import { WaitlistRequestStatus } from 'routes/business/search/ExperienceSearchRoute';
import { adjustDateForCutOff } from 'utils/constants';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { isPaymentCardHoldExperience } from 'utils/priceUtils';
import { memoizeWithTimezoneGuard } from '@tock/shared/utils/timezone';
import { pluralize } from '@tock/shared/utils/pluralize';
import Anchor from 'components/Anchor';
import BaseComponent from '@tock/shared/components/BaseComponent';
import Button from '@tock/shared/components/buttons/DeprecatedButton';
import LinkButton from '@tock/shared/components/buttons/LinkButton';

const getSeatingAreaIdsFromTG = (
  ticketGroups: consumer.ICalendarTicketGroup[],
  selectedPurchaseSize: number
) => {
  const availableTicketGroups = ticketGroups.filter(
    (ticketGroup) =>
      ticketGroupStatusForPurchaseSize(ticketGroup, selectedPurchaseSize) ===
      TicketGroupStatus.AVAILABLE
  );

  const seatingAreaIds = availableTicketGroups
    .map((ticketGroup) => ticketGroup.seatingAreaId)
    .filter(filterNilValues);
  return uniq(seatingAreaIds);
};

const filterSeatingAreasByIds = (
  seatingAreas: core.IAvailabilitySeatingArea[],
  seatingAreaIds: number[]
) => seatingAreas.filter((s) => s.id && seatingAreaIds.includes(s.id));

const getPriceRange = (
  ticketGroups: consumer.CalendarTicketGroup[],
  selectedPurchaseSize: number
): { min: number; max: number } => {
  const availableTicketGroups = filter(
    ticketGroups,
    (ticketGroup) =>
      ticketGroupStatusForPurchaseSize(ticketGroup, selectedPurchaseSize) ===
      TicketGroupStatus.AVAILABLE
  );

  if (availableTicketGroups.length > 0) {
    const prices = availableTicketGroups.map(
      (ticketGroup) => ticketGroup.ticketTypePrice[0].priceCents || 0
    );
    return {
      min: _min(prices) || 0,
      max: _max(prices) || 0,
    };
  }

  const prices = ticketGroups.map((ticketGroup) => ticketGroup.ticketTypePrice[0].priceCents || 0);
  return {
    min: _min(prices) || 0,
    max: _max(prices) || 0,
  };
};

const getMinRequiredSpendPerPerson = (
  ticketGroups: consumer.CalendarTicketGroup[],
  selectedPurchaseSize: number
) => {
  const availableTicketGroups = ticketGroups.filter(
    (ticketGroup) =>
      ticketGroupStatusForPurchaseSize(ticketGroup, selectedPurchaseSize) ===
      TicketGroupStatus.AVAILABLE
  );

  const effectiveTicketGroups =
    availableTicketGroups.length > 0 ? availableTicketGroups : ticketGroups;

  const totalCents = _max(
    effectiveTicketGroups.map(
      (ticketGroup) =>
        ticketGroup.ticketTypePrice?.[0].minRequiredSpend?.totalCentsByPartySize?.[
          selectedPurchaseSize
        ]
    )
  );

  const priceDisplayDistribution =
    ticketGroups?.[0].ticketTypePrice?.[0].minRequiredSpend?.distribution ?? undefined;

  return {
    totalCents,
    priceDisplayDistribution,
  };
};

/**
 * All ticket groups are expected to have the same display style encoded.
 */
const getPriceDisplayStyle = (
  ticketGroups: consumer.CalendarTicketGroup[]
): consumer.OfferedPriceSummary.PriceDisplayStyle => {
  return ticketGroups.find(
    (ticketGroup) => ticketGroup.ticketTypePrice?.[0].priceDisplayStyle === 'MIN_REQUIRED_SPEND'
  )
    ? 'MIN_REQUIRED_SPEND'
    : 'BASE_TICKET_PRICE';
};

const getTicketPriceInformation = (
  ticketGroups: consumer.CalendarTicketGroup[],
  selectedPurchaseSize: number
) => {
  const availableTicketGroups = ticketGroups.filter(
    (ticketGroup) =>
      ticketGroupStatusForPurchaseSize(ticketGroup, selectedPurchaseSize) ===
      TicketGroupStatus.AVAILABLE
  );

  const effectiveTicketGroups =
    availableTicketGroups.length > 0 ? availableTicketGroups : ticketGroups;

  const mostExpensiveTicketGroup = maxBy(
    effectiveTicketGroups,
    (ticketGroup) => ticketGroup.ticketTypePrice?.[0].priceCents ?? 0
  );

  return mostExpensiveTicketGroup?.ticketTypePrice?.[0].ticketPriceInformation ?? undefined;
};

interface VisibleTime {
  time: string | null;
  priceInfo: {
    priceRange: {
      min: number;
      max: number;
    };
    minRequiredSpend?: {
      totalCents?: number;
      priceDisplayDistribution?: core.TicketPriceInformation.PriceDistribution;
    };
    priceDisplayStyle: consumer.OfferedPriceSummary.PriceDisplayStyle;
    ticketPriceInformation?: core.ITicketPriceInformation;
  };
  isAvailable: boolean;
  seatingAreaIds: number[];
}

const getVisibleTimes = memoizeWithTimezoneGuard(
  (
    times: SearchTimeMap,
    selectedDate: string,
    selectedTime: string,
    selectedPurchaseSize: number,
    selectedSeatingAreaId?: number
  ) => {
    const availableTimes = fromPairs(
      filter(
        map(times, (entry, time) => [time, entry]),
        (pair) => {
          const matchesSeatingAreaSelection: boolean =
            selectedSeatingAreaId === undefined ||
            (pair[1] as any).ticketGroups.some(
              (group: consumer.CalendarTicketGroup) => group.seatingAreaId === selectedSeatingAreaId
            );

          return ((pair[1] as any).isAvailable as boolean) && matchesSeatingAreaSelection;
        }
      )
    );

    const selectedDateTime: LocalDateTime = adjustDateForCutOff(selectedDate, selectedTime);
    const min = LocalDateTime.parse(_min(keys(times)) as string);
    const max = LocalDateTime.parse(_max(keys(times)) as string);

    let visibleTimes: VisibleTime[] = [];
    const cursor = selectedDateTime;
    let cursorDown = selectedDateTime;
    let cursorUp = selectedDateTime;
    let selectedTimeAdded = false;
    while (!cursorDown.isBefore(min) || !cursorUp.isAfter(max)) {
      const dateTime = cursor.toString();

      cursorDown = cursorDown.minusMinutes(15);
      const dateTimeDown = cursorDown.toString();

      cursorUp = cursorUp.plusMinutes(15);
      const dateTimeUp = cursorUp.toString();

      if (!cursorUp.isAfter(max) && has(availableTimes, dateTimeUp)) {
        visibleTimes.push({
          time: dateTimeUp,
          priceInfo: {
            priceRange: getPriceRange(
              availableTimes[dateTimeUp]?.ticketGroups || [],
              selectedPurchaseSize
            ),
            minRequiredSpend: getMinRequiredSpendPerPerson(
              availableTimes[dateTimeUp]?.ticketGroups || [],
              selectedPurchaseSize
            ),
            priceDisplayStyle: getPriceDisplayStyle(availableTimes[dateTimeUp]?.ticketGroups || []),
            ticketPriceInformation: getTicketPriceInformation(
              availableTimes[dateTimeUp]?.ticketGroups || [],
              selectedPurchaseSize
            ),
          },
          isAvailable: true,
          seatingAreaIds: getSeatingAreaIdsFromTG(
            times[dateTimeUp].ticketGroups,
            selectedPurchaseSize
          ),
        });
        if (visibleTimes.length > 2) {
          break;
        }
      }

      if (!selectedTimeAdded && has(availableTimes, dateTime)) {
        selectedTimeAdded = true;
        visibleTimes.push({
          time: dateTime,
          priceInfo: {
            priceRange: getPriceRange(
              availableTimes[dateTime]?.ticketGroups || [],
              selectedPurchaseSize
            ),
            minRequiredSpend: getMinRequiredSpendPerPerson(
              availableTimes[dateTime]?.ticketGroups || [],
              selectedPurchaseSize
            ),
            priceDisplayStyle: getPriceDisplayStyle(availableTimes[dateTime]?.ticketGroups || []),
            ticketPriceInformation: getTicketPriceInformation(
              availableTimes[dateTime]?.ticketGroups || [],
              selectedPurchaseSize
            ),
          },
          isAvailable: true,
          seatingAreaIds: getSeatingAreaIdsFromTG(
            times[dateTime].ticketGroups,
            selectedPurchaseSize
          ),
        });
        if (visibleTimes.length > 2) {
          break;
        }
      }

      if (!cursorDown.isBefore(min) && has(availableTimes, dateTimeDown)) {
        visibleTimes.push({
          time: dateTimeDown,
          priceInfo: {
            priceRange: getPriceRange(
              availableTimes[dateTimeDown]?.ticketGroups || [],
              selectedPurchaseSize
            ),
            minRequiredSpend: getMinRequiredSpendPerPerson(
              availableTimes[dateTimeDown]?.ticketGroups || [],
              selectedPurchaseSize
            ),
            priceDisplayStyle: getPriceDisplayStyle(
              availableTimes[dateTimeDown]?.ticketGroups || []
            ),
            ticketPriceInformation: getTicketPriceInformation(
              availableTimes[dateTimeDown]?.ticketGroups || [],
              selectedPurchaseSize
            ),
          },
          isAvailable: true,
          seatingAreaIds: getSeatingAreaIdsFromTG(
            times[dateTimeDown].ticketGroups,
            selectedPurchaseSize
          ),
        });
        if (visibleTimes.length > 2) {
          break;
        }
      }
    }

    visibleTimes = sortBy(visibleTimes, ['time']);

    if (visibleTimes.length < 3 && visibleTimes.length > 0) {
      if (visibleTimes.length === 1) {
        visibleTimes.push({
          time: null,
          priceInfo: {
            priceRange: null as any,
            minRequiredSpend: null as any,
            priceDisplayStyle: null as any,
            ticketPriceInformation: null as any,
          },
          isAvailable: false,
          seatingAreaIds: [],
        });
      }
      if (visibleTimes.length === 2) {
        visibleTimes.push({
          time: null,
          priceInfo: {
            priceRange: null as any,
            minRequiredSpend: null as any,
            priceDisplayStyle: null as any,
            ticketPriceInformation: null as any,
          },
          isAvailable: false,
          seatingAreaIds: [],
        });
      }
    }

    return visibleTimes;
  }
);

const getWithinRangeTimes = memoizeWithTimezoneGuard(
  (visibleTimes: VisibleTime[], selectedDate: string, selectedTime: string) => {
    const selectedDateTime = adjustDateForCutOff(selectedDate, selectedTime);

    const withinRange = filter(visibleTimes, (visibleTime) => {
      if (visibleTime.time === null) {
        return false;
      }
      const time = LocalDateTime.parse(visibleTime.time);
      return Math.abs(time.until(selectedDateTime, ChronoUnit.MINUTES)) < 180;
    });
    return withinRange.length > 0;
  }
);

/**
 * Returns the smallest number of minutes between the requested time and what
 * is available.
 */
export const getNearestTimeDelta = memoizeWithTimezoneGuard(
  (visibleTimes: VisibleTime[], selectedDate: string, selectedTime: string) => {
    const selectedDateTime = adjustDateForCutOff(selectedDate, selectedTime);

    const deltas: number[] = [];
    visibleTimes.forEach((visibleTime) => {
      if (visibleTime.time !== null) {
        const startTime = LocalDateTime.parse(visibleTime.time);
        deltas.push(Math.abs(startTime.until(selectedDateTime, ChronoUnit.MINUTES)));
      }
    });

    return deltas.length > 0 ? Math.min(...deltas) : undefined;
  }
);

interface Props {
  availableDates: DateMap;
  availableTimes: Dictionary<Stats>;
  businessEmail?: string | null;
  businessPhone: string;
  businessId?: number;
  callToActionHint?: string;
  exclusivityAvailable: core.enums.Exclusivity[];
  isEventVariety: boolean;
  isOfferedAtOneDateTime: boolean;
  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
  offering: consumer.Offering;
  onNavigateToExperience(event: any, nextAvailableDate?: string): void;
  onSelect(dateTime: LocalDateTime, priceCents: number, options: LockOptions): void;
  onShowWaitlist: ReservationSearchResultsProps['onShowWaitlist'];
  seatingAreas?: core.IAvailabilitySeatingArea[];
  selectedDate: string;
  selectedPartySize: number;
  selectedPurchaseSize: number;
  selectedSeatingAreaId?: number;
  selectedTime: string;
  showWaitlist: boolean;
  ticketType: core.TicketType;
  times: SearchTimeMap;
}

class SearchResultMini extends BaseComponent<Props & IWithContextProps> {
  handleNavigateToNextExperience() {
    const { availableDates } = this.props;

    const nextAvailableDate = head(sortBy(keys(pickBy(availableDates))));
    if (nextAvailableDate) {
      this.props.onNavigateToExperience(this.props.ticketType, nextAvailableDate);
    }
  }

  handleNavigateToExperience() {
    this.props.onNavigateToExperience(this.props.ticketType);
  }

  handleShowWaitlist(status: WaitlistRequestStatus, nearestAvailabilityMinutes?: number) {
    this.props.onShowWaitlist(this.props.ticketType.id, { status, nearestAvailabilityMinutes });
  }

  render() {
    const {
      availableTimes,
      availableDates,
      businessEmail,
      businessPhone,
      businessId,
      callToActionHint,
      isEventVariety,
      isOfferedAtOneDateTime,
      lockedDateTime,
      lockedSeatingArea,
      offering,
      onSelect,
      seatingAreas = [],
      selectedDate,
      selectedPartySize,
      selectedPurchaseSize,
      selectedTime,
      selectedSeatingAreaId,
      showWaitlist,
      times,
      ticketType,
    } = this.props;

    const singleUnitQuantity = ticketType.singleUnitQuantity ?? 1;
    const totalAvailability = filter(times, (entry) => !!entry.isAvailable).length;
    const visibleTimes = getVisibleTimes(
      times,
      selectedDate,
      selectedTime,
      selectedPurchaseSize,
      selectedSeatingAreaId
    );

    const additionalAvailability = totalAvailability - visibleTimes.length;
    const additionalMessage = pluralize(additionalAvailability, 'more time');
    const withinRangeTimes = getWithinRangeTimes(visibleTimes, selectedDate, selectedTime);
    const nearestTimeDeltaMinutes = getNearestTimeDelta(visibleTimes, selectedDate, selectedTime);

    const treatAsEvent =
      isOfferedAtOneDateTime &&
      (isEventVariety || this.props.context.isFeatureEnabled('EVENT_LIKE_EXPERIENCES'));
    const showPhoneAndEmail = this.props.context.isFeatureEnabled(
      'SHOW_PHONE_AND_EMAIL_NO_RESERVATIONS'
    );

    const otherDatesAvailable = availableDates && !isEmpty(availableDates);
    const isShipping = !!ticketType.reserveShippingTime;

    if (isEmpty(availableTimes)) {
      return (
        <div>
          <div className="Consumer-noResultsListItem">
            <Typography component="span" variant="body02" color="inherit">
              <span>Sold out for parties of {selectedPurchaseSize}</span>
              {!treatAsEvent && [
                <span key="spacer">&nbsp;on&nbsp;</span>,
                <DisplayDate
                  key="date"
                  dateValue={selectedDate}
                  displayMonthAs="short"
                  showYear={false}
                />,
              ]}
            </Typography>
          </div>
          <div className="SearchModal-linkContainer">
            {otherDatesAvailable && (
              <LinkButton
                className="SearchModal-link"
                onClick={this.handleNavigateToNextExperience}
              >
                <Typography component="span" variant="body02" color="inherit">
                  Next available
                </Typography>
              </LinkButton>
            )}
            {showWaitlist && (
              <LinkButton
                className="SearchModal-link SearchModal-waitlistLink"
                onClick={() => this.handleShowWaitlist('SOLD_OUT')}
              >
                <Typography component="span" variant="body02" color="inherit">
                  Notify
                </Typography>
              </LinkButton>
            )}
          </div>
        </div>
      );
    }

    // There's no need to check for the sold out case because it's caught above when `availableTimes` is empty
    if (treatAsEvent) {
      return (
        <div className="Consumer-resultsList">
          <button
            className="Consumer-reservationCallToAction"
            onClick={this.handleNavigateToExperience}
          >
            <Button size="small" variant="primary">
              Book now
            </Button>
            {callToActionHint && (
              <div className="Consumer-reservationHint">
                <Typography component="span" variant="body02" color="inherit">
                  {callToActionHint}
                </Typography>
              </div>
            )}
          </button>
        </div>
      );
    }

    return (
      <div>
        {!withinRangeTimes && (
          <div className="SearchModalExperiences-distantTimesDisclaimer">
            {showPhoneAndEmail ? (
              <Typography component="span" variant="body02" color="inherit">
                There are no reservations near the selected time. Below are the available options.
                {businessEmail && (
                  <span>
                    We also welcome you to{' '}
                    <Anchor href={`mailto:${businessEmail}`}>send us an email</Anchor>{' '}
                  </span>
                )}
                {businessPhone && (
                  <span>
                    or call us at <Anchor href={`tel:${businessPhone}`}>{businessPhone}</Anchor>
                  </span>
                )}
                .
              </Typography>
            ) : (
              <Typography component="span" variant="body02" color="inherit">
                There are no reservations near the selected time. Here are the available options:
              </Typography>
            )}
          </div>
        )}
        <div className="Consumer-resultsListVertical">
          {visibleTimes
            .filter((t) => !!t.time)
            .map((visibleTime, key) => (
              <BookingCard
                businessId={businessId}
                experience={offering}
                seatingAreas={filterSeatingAreasByIds(seatingAreas, visibleTime.seatingAreaIds)}
                isAvailable={visibleTime.isAvailable}
                priceInfo={visibleTime.priceInfo}
                time={visibleTime.time}
                isShipping={isShipping}
                key={visibleTime.time || key}
                onClick={onSelect}
                partySize={selectedPartySize}
                purchaseSize={selectedPurchaseSize}
                showWaitlist={showWaitlist}
                singleUnitQuantity={singleUnitQuantity}
                userLockedDateTime={lockedDateTime}
                userLockedSeatingArea={lockedSeatingArea}
                hidePrice={isPaymentCardHoldExperience(offering)}
              />
            ))}
        </div>
        <div className="SearchModal-linkContainer">
          {additionalAvailability > 0 && (
            <LinkButton className="SearchModal-link" onClick={this.handleNavigateToExperience}>
              <Typography component="span" variant="body02" color="inherit">
                {additionalMessage}
              </Typography>
            </LinkButton>
          )}
          {showWaitlist && (
            <LinkButton
              className="SearchModal-link SearchModal-waitlistLink"
              onClick={() =>
                this.handleShowWaitlist(
                  nearestTimeDeltaMinutes === 0 ? 'AVAILABLE' : 'PARTIALLY_SOLD_OUT',
                  nearestTimeDeltaMinutes
                )
              }
            >
              <Typography component="span" variant="body02" color="inherit">
                Notify
              </Typography>
            </LinkButton>
          )}
        </div>
      </div>
    );
  }
}

export default withContext(SearchResultMini);
